import { IdType } from "vis-network";
import { getNodeSize } from "./utils";

import {
  CreatePieNodeProps,
  GenerateNodesFromDataFn,
  GeneratePieNodeProps,
  RlNode
} from "./@types";
import { pieNodeCtxRenderer } from "./node-renderers";

const connectLeafsToParent = (nodes: RlNode[], parentNodeId: IdType) => ({
  nodes,
  edges: nodes.map(node => ({
    from: parentNodeId,
    to: node.id,
    length: node.type === "control" ? 300 : 250
  }))
});

const emptyControlNode = (id: IdType, label: string, total: number) => ({
  id,
  label,
  shape: "dot",
  fixed: false,
  type: "control",
  color: "lightgrey",
  size: 15,
  total
});
/**
 * Precalculated positions for static graph on ReportSummary page
 */
export const reportSummaryNodePos = {
  rootNode: {
    x: -219,
    y: -265
  },
  extractedFilesNode: {
    x: -525,
    y: -420
  },
  parentsNode: {
    x: -123,
    y: -56
  },
  rha1SimilarityNode: {
    x: -218,
    y: -519
  },
  droppedFilesNode: {
    x: -319,
    y: -57
  },
  networkConnectionsNode: {
    x: 137,
    y: -156
  },
  networkReferencesNode: {
    x: -576,
    y: -172
  },
  networkDynamicNode: {
    x: 91,
    y: -421
  }
};
const createPieNode = ({
  id,
  x,
  y,
  label,
  points,
  total,
  radius,
  extra
}: CreatePieNodeProps) => ({
  id,
  label,
  extra,
  total,
  shape: "custom",
  fixed: false,
  type: "control",
  size: 100, // used as bounding box for network.fit calculation
  x,
  y,
  ctxRenderer: pieNodeCtxRenderer({
    points,
    total,
    label,
    radius
  })
});

const generateStaticPieNode = ({
  id,
  label,
  points,
  total,
  radius,
  extra
}: GeneratePieNodeProps) =>
  total
    ? createPieNode({
        id,
        x: reportSummaryNodePos[id as keyof typeof reportSummaryNodePos].x,
        y: reportSummaryNodePos[id as keyof typeof reportSummaryNodePos].y,
        label,
        points,
        total,
        radius: radius || getNodeSize(total),
        extra
      })
    : emptyControlNode(id, label, total);

const generatePieNode = ({
  id,
  label,
  points,
  total,
  radius,
  extra
}: GeneratePieNodeProps) =>
  total
    ? createPieNode({
        id,
        label,
        points,
        total,
        radius: radius || getNodeSize(total),
        extra
      })
    : emptyControlNode(id, label, total);

const generateNodesFromData: GenerateNodesFromDataFn = (
  data,
  mapperFn,
  nodeOptions = {}
) =>
  data.map(obj => {
    const nodeData = mapperFn(obj);
    return {
      fixed: false,
      type: "leaf",
      shape: "image",
      ...nodeData.visNodeData,
      ...nodeOptions,
      extra: nodeData.sidebarData
    };
  });

export {
  connectLeafsToParent,
  generateNodesFromData,
  generatePieNode,
  generateStaticPieNode,
  createPieNode
};
